import { useEffect, useState } from 'react';
import { $getNodeByKey } from 'lexical';

import { StringsToOptions } from 'src/compass/shared/util/StringsToOptions';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import { getParsedHTML } from 'src/compass/shared/util/getParsedHTML';
import { useApiContext } from 'src/compass/site/providers/ApiContextProvider/ApiContextProvider';

import { CasSecondaryButton } from 'src/compass/shared/components/CasButton/CasSecondaryButton';

import CasSelect from '../../../CasSelect/CasSelect';
import CasPanel from '../../../CasPanel/CasPanel';
import { $isCasTableDataNode, IDataTableConfig } from './CasTableDataNode';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

interface Props {
  dataObject: IDataTableConfig['dataObject'];
  renderDataObjectUrl: string;
  nodeKey: string;
}

export enum CasTableNodeLayout {
  Basic = 'Basic',
  Current_Value = 'Current Value',
  Prelim_Settle = 'Prelim Settle',
}

const LAYOUT_OPTIONS = StringsToOptions([
  CasTableNodeLayout.Basic,
  CasTableNodeLayout.Current_Value,
  CasTableNodeLayout.Prelim_Settle,
]);

export function DataObjectRenderer({ renderDataObjectUrl, dataObject, nodeKey }: Props) {
  const [editor] = useLexicalComposerContext();
  const [layout, setLayout] = useState(dataObject.layout ?? '');

  const { data, isPending, mutate } = useAddMutation({
    url: renderDataObjectUrl,
    showLoader: false,
  });

  // Download the PDF this table would be attached to the email as, so the sender can review it
  // before sending. Hits the sibling RenderPdf endpoint with this table's exact dataObject, so
  // what downloads is byte-for-byte what the recipient gets.
  const { api } = useApiContext();
  const [pdfPending, setPdfPending] = useState(false);
  const downloadPdf = async () => {
    setPdfPending(true);
    try {
      const url = renderDataObjectUrl.replace(/RenderDataObject$/, 'RenderPdf');
      const response = await api.post(url, { ...dataObject, layout }, { responseType: 'blob' });
      const href = URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = 'SCE Report.pdf';
      anchor.click();
      URL.revokeObjectURL(href);
    } finally {
      setPdfPending(false);
    }
  };

  useEffect(() => {
    mutate({ ...dataObject, layout });
  }, [mutate]);

  const result = data as { html: string; docUrl?: string } | undefined;

  return (
    <CasPanel variant="outlined" maxHeight="200px" data-table-key={nodeKey}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <CasSelect
          label="Layout"
          value={layout}
          onChange={(_, value) => {
            setLayout(value);
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);
              if ($isCasTableDataNode(node)) {
                const writable = node.getWritable();
                writable.__dataObject = {
                  ...writable.__dataObject,
                  layout: value,
                };
              }
            });
            mutate({ ...dataObject, layout: value });
          }}
          options={LAYOUT_OPTIONS}
        />
        {result?.docUrl && (
          <a
            href={result.docUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '12px', whiteSpace: 'nowrap', textDecoration: 'none' }}
          >
            Column Definitions
          </a>
        )}
        <CasSecondaryButton
          size="small"
          onClick={() => downloadPdf()}
          disabled={pdfPending}
          sx={{ py: 0.125, px: 1, fontSize: 11, minWidth: 0, whiteSpace: 'nowrap', lineHeight: 1.6 }}
        >
          {pdfPending ? 'Generating…' : 'Review PDF'}
        </CasSecondaryButton>
      </div>
      {isPending || !result ? (
        <CasLoader />
      ) : (
        <div style={{ fontSize: '12px' }}>{getParsedHTML(result.html)}</div>
      )}
    </CasPanel>
  );
}
